package com.neurobs.ccabserver.transcriptreview.states

import com.neurobs.ccab.{AccountDataRest, CCABServer, StudyView}
import com.neurobs.ccab.casr.{AccountSubjectChoice, AccountSubjectTestData, ReviewedTestTranscriptMetadata, TestChoice, TestTranscriptMetadata}
import com.neurobs.ccab.db.{AccountData, ReviewedTestTranscriptData, TestData}
import com.neurobs.react.{StateAttribute, StateContextBase}
import org.scalajs.dom.console

import scala.concurrent.ExecutionContext

object FilterType extends Enumeration {
	val Test = Value("Test")
	val AccountSubject = Value("Account-Subject")
}

case class TestTranscriptSelection
(
	transcript: TestTranscriptMetadata,
	reviewed: Seq[ReviewedTestTranscriptMetadata]
)

case class TranscriptSelectionData
(
	allTests: Seq[TestData],
	account: Option[AccountDataRest] = None,
	study: Option[StudyView] = None,
	reviewedStatus: Option[Boolean] = None,
	subjectTestData: Option[AccountSubjectTestData] = None,
	primaryFilterType: FilterType.Value = FilterType.AccountSubject,
	test: Option[TestChoice] = None,
	testChoices: Option[Seq[TestChoice]] = None,
	accountSubject: Option[AccountSubjectChoice] = None,
	accountSubjectChoices: Option[Seq[AccountSubjectChoice]] = None,
	testTranscript: Option[TestTranscriptSelection] = None,
	testTranscriptChoices: Option[Seq[TestTranscriptSelection]] = None
)
{
	import TranscriptSelectionData._

	def changedReviewedStatus(newStatus: Option[Boolean]): TranscriptSelectionData = {
		if (newStatus == reviewedStatus) this
		else TranscriptSelectionData(
			allTests = allTests,
			account = account,
			study = study,
			reviewedStatus = newStatus,
			primaryFilterType = primaryFilterType
		)
	}

	def changedAccount(newAccount: Option[AccountDataRest]): TranscriptSelectionData = {
		if (newAccount == account) this
		else TranscriptSelectionData(
			allTests = allTests,
			account = newAccount,
			reviewedStatus = reviewedStatus,
			primaryFilterType = primaryFilterType
		)
	}

	def changedStudy(newStudy: Option[StudyView]): TranscriptSelectionData = {
		if (newStudy == study) this
		else TranscriptSelectionData(
			allTests = allTests,
			account = account,
			study = newStudy,
			reviewedStatus = reviewedStatus,
			primaryFilterType = primaryFilterType
		)
	}

	private def changedFilters(newSubjectTestData: Option[AccountSubjectTestData], newPrimaryFilterType: FilterType.Value, requestedTest: Option[TestChoice], requestedAccountSubject: Option[AccountSubjectChoice]): TranscriptSelectionData = {
		if ((newSubjectTestData == subjectTestData) && (primaryFilterType == newPrimaryFilterType) && (test == requestedTest) && (accountSubject == requestedAccountSubject)) this
		else newSubjectTestData match {
			case None =>
				TranscriptSelectionData(
					allTests = allTests,
					account = account,
					study = study,
					reviewedStatus = reviewedStatus,
					primaryFilterType = newPrimaryFilterType
				)
			case Some(data) =>
				val (newTestChoices, newTest, newAccountSubjectChoices, newAccountSubject) = newPrimaryFilterType match {
					case FilterType.Test =>
						val newTestChoices = Some(data.testChoices(subject = None, tests = allTests))
						val newTest = preserveSelection(newTestChoices, requestedTest)
						val newAccountSubjectChoices =
							if (newTest.isEmpty) None
							else Some(data.subjectChoices(test = newTest))
						val newAccountSubject = preserveSelection(newAccountSubjectChoices, requestedAccountSubject)
						(newTestChoices, newTest, newAccountSubjectChoices, newAccountSubject)
					case FilterType.AccountSubject =>
						val newAccountSubjectChoices = Some(data.subjectChoices(test = None))
						val newAccountSubject = preserveSelection(newAccountSubjectChoices, requestedAccountSubject)
						val newTestChoices =
							if (newAccountSubject.isEmpty) None
							else Some(data.testChoices(subject = newAccountSubject, tests = allTests))
						val newTest = preserveSelection(newTestChoices, requestedTest)
						(newTestChoices, newTest, newAccountSubjectChoices, newAccountSubject)
				}
				val preserveTranscripts = (newPrimaryFilterType == primaryFilterType) && (newPrimaryFilterType match {
					case FilterType.Test => newTest == test
					case FilterType.AccountSubject => newAccountSubject == accountSubject
				})
				val newTestTranscriptChoices = if (preserveTranscripts) testTranscriptChoices else None
				val filtered = filteredTestTranscriptSelections(newTestTranscriptChoices, newTest, newAccountSubject)
				val newTestTranscript = preserveSelection(filtered, testTranscript)
				TranscriptSelectionData(
					allTests = allTests,
					account = account,
					study = study,
					reviewedStatus = reviewedStatus,
					subjectTestData = newSubjectTestData,
					primaryFilterType = newPrimaryFilterType,
					test = newTest,
					testChoices = newTestChoices,
					accountSubject = newAccountSubject,
					accountSubjectChoices = newAccountSubjectChoices,
					testTranscript = newTestTranscript,
					testTranscriptChoices = newTestTranscriptChoices
				)
		}
	}

	def changedPrimaryFilterType(newPrimaryFilterType: FilterType.Value): TranscriptSelectionData = {
		changedFilters(subjectTestData, newPrimaryFilterType, test, accountSubject)
	}

	def changedTest(newTest: Option[TestChoice]): TranscriptSelectionData = {
		changedFilters(subjectTestData, primaryFilterType, newTest, accountSubject)
	}

	def changedAccountSubject(newAccountSubject: Option[AccountSubjectChoice]): TranscriptSelectionData = {
		changedFilters(subjectTestData, primaryFilterType, test, newAccountSubject)
	}

	def changedTestTranscript(newTestTranscript: Option[TestTranscriptSelection]): TranscriptSelectionData = {
		if (testTranscript == newTestTranscript) this
		else copy(testTranscript = preserveSelection(testTranscriptChoices, newTestTranscript))
	}

	def changedTestTranscriptChoices(newTestTranscriptChoices: Option[Seq[TestTranscriptSelection]]): TranscriptSelectionData = {
		if (testTranscriptChoices == newTestTranscriptChoices) this
		else copy(
			testTranscriptChoices = newTestTranscriptChoices,
			testTranscript = preserveSelection(newTestTranscriptChoices, testTranscript)
		)
	}

	def changedSubjectTestData(newSubjectTestData: Option[AccountSubjectTestData]): TranscriptSelectionData = {
		changedFilters(newSubjectTestData, primaryFilterType, test, accountSubject)
	}
}

object TranscriptSelectionData {
	/** If both choices and sel have values, and sel is in choices, returns sel, otherwise None
	 *
	 * @param choices Options or None
	 * @param sel Selection or None
	 * @return New selection
	 */
	def preserveSelection[T](choices: Option[Seq[T]], sel: Option[T]): Option[T] = {
		sel.flatMap(t => choices.flatMap(c => c.find(_ == t)))
	}

	def filteredTestTranscriptSelections(all: Option[Seq[TestTranscriptSelection]], test: Option[TestChoice], subject: Option[AccountSubjectChoice]): Option[Seq[TestTranscriptSelection]] = {
		all.map(selections => {
			val filter1 = subject match {
				case None => selections
				case Some(s) => selections.filter(_.transcript.accountSubjectId == s.accountSubjectId)
			}
			test match {
				case None => filter1
				case Some(t) => filter1.filter(_.transcript.testId == t.testId)
			}
		})
	}
}

case class TranscriptSelectionState
(
	data: StateAttribute[TranscriptSelectionData]
)
{
	private def setData(newData: TranscriptSelectionData): Unit = {
		if (newData != data.value) data.setValue(newData)
	}

	def selectAccount(accountData: AccountDataRest): Unit = {
		setData(data.value.changedAccount(Some(accountData)))
	}
	def clearAccount(): Unit = {
		setData(data.value.changedAccount(None))
	}

	def selectStudy(studyView: StudyView): Unit = {
		setData(data.value.changedStudy(Some(studyView)))
	}
	def clearStudy(): Unit = {
		setData(data.value.changedStudy(None))
	}

	def setReviewedStatus(reviewed: Option[Boolean]): Unit = {
		setData(data.value.changedReviewedStatus(reviewed))
	}

	def setPrimaryFilter(filter: FilterType.Value): Unit = {
		setData(data.value.changedPrimaryFilterType(filter))
	}
	def setTestAsPrimary(): Unit = setPrimaryFilter(FilterType.Test)
	def setAccountSubjectAsPrimary(): Unit = setPrimaryFilter(FilterType.AccountSubject)
	def testFilterIsPrimary(): Boolean = data.value.primaryFilterType == FilterType.Test
	def accountSubjectFilterIsPrimary(): Boolean = data.value.primaryFilterType == FilterType.AccountSubject

	def setSubjectTestData(newSubjectTestData: Option[AccountSubjectTestData]): Unit = {
		setData(data.value.changedSubjectTestData(newSubjectTestData))
	}

	def selectTestFilter(choice: Option[TestChoice]): Unit = {
		setData(data.value.changedTest(choice))
	}

	def selectAccountSubjectFilter(choice: Option[AccountSubjectChoice]): Unit = {
		setData(data.value.changedAccountSubject(choice))
	}

	def selectTestTranscript(selection: TestTranscriptSelection): Unit = {
		setData(data.value.changedTestTranscript(Some(selection)))
	}
	def clearSelectedTestTranscript(): Unit = {
		setData(data.value.changedTestTranscript(None))
	}

	def updateReviewedTestTranscriptId(transcript: ReviewedTestTranscriptData, username: String): Unit = {
		def update(s: TestTranscriptSelection): TestTranscriptSelection = {
			val newItem = ReviewedTestTranscriptMetadata(reviewedTestTranscriptId = transcript.id, createdAt = transcript.createdAt, testRunId = transcript.testRunId, userId = transcript.userId, forLatency = transcript.forLatency, username = username)
			val newReviewed = s.reviewed.filter(_.reviewedTestTranscriptId != newItem.reviewedTestTranscriptId) :+ newItem
			s.copy(reviewed = newReviewed)
		}

		val newChoices = data.value.testTranscriptChoices.map(_.map { s =>
			if (s.transcript.testRunId == transcript.testRunId) update(s) else s
		})
		val newSel = data.value.testTranscript map { t =>
			if (t.transcript.testRunId == transcript.testRunId) update(t) else t
		}
		setData(data.value.copy(testTranscriptChoices = newChoices, testTranscript = newSel))
		//println(s"updateReviewedTestTranscriptId ${transcript.id}")
	}

	def setTestTranscriptSelections(selections: Seq[TestTranscriptSelection]): Unit = {
		setData(data.value.changedTestTranscriptChoices(Some(selections)))
	}
	def clearTestTranscriptSelections(): Unit = {
		setData(data.value.changedTestTranscriptChoices(None))
	}

	def filteredTestTranscriptSelections(): Option[Seq[TestTranscriptSelection]] = {
		TranscriptSelectionData.filteredTestTranscriptSelections(data.value.testTranscriptChoices, data.value.test, data.value.accountSubject)
	}

	private def checkPrimaryFilterSelection(): Option[Either[TestChoice, AccountSubjectChoice]] = {
		data.value.primaryFilterType match {
			case FilterType.Test => data.value.test.map(Left(_))
			case FilterType.AccountSubject => data.value.accountSubject.map(Right(_))
		}
	}

	private def checkNecessarySelections(): Option[(AccountDataRest, StudyView, Option[TestChoice], Option[AccountSubjectChoice])] = {
		for {
			account <- data.value.account
			study <- data.value.study
			filter <- checkPrimaryFilterSelection()
		} yield filter match {
			case Left(testChoice) => (account, study, Some(testChoice), None)
			case Right(accountSubjectChoice) => (account, study, None, Some(accountSubjectChoice))
		}
	}

	def loadTestTranscriptSelections(server: CCABServer)(implicit errorState: ErrorState,  executor: ExecutionContext): Unit = {
		checkNecessarySelections() match {
			case None => console.log(message = "Don't have the necessary selections")
			case Some((accountData, studyView, testChoiceOpt, accountSubjectChoiceOpt)) =>
				val testIdOpt = testChoiceOpt.map(_.testId)
				val accountSubjectIdOpt = accountSubjectChoiceOpt.map(_.accountSubjectId)
				server.getTestTranscriptSelections(
					accountId = accountData.id,
					studyId = studyView.id,
					testId = testIdOpt,
					accountSubjectId = accountSubjectIdOpt
				)._2.map {
					case Left(error) => errorState.addError(error.description)
					case Right(res) =>
						// If multiple transcripts for the same test run, use the most recently made
						val latestTestTranscripts = res.testTranscripts.groupBy(_.testRunId).map(pair => {
							pair._2.sortBy(_.createdAt).last
						}).toSeq
						val sels = latestTestTranscripts map { t =>
							TestTranscriptSelection(transcript = t, reviewed = res.reviewedTranscripts.filter(_.testRunId == t.testRunId))
						}
						val filtered = data.value.reviewedStatus match {
							case None => sels
							case Some(reviewed) => sels.filter(_.reviewed.nonEmpty == reviewed)
						}
						setTestTranscriptSelections(filtered)
				}
		}
	}
}

object TranscriptSelectionState extends StateContextBase[TranscriptSelectionState, TranscriptSelectionData] {
	override protected def defaultStateValue() = TranscriptSelectionData(allTests = Seq())

	override protected def makeState(a: StateAttribute[TranscriptSelectionData]) = new TranscriptSelectionState(a)
}